// The pre-join page (上木筏): what you check before 上船, in the join card. Your otter turning on the spot with
// 装扮 (the dress-up panel, public/dress.js; the first time, with no look saved yet, it suggests dressing up), your
// name and status (在线 / 离开 / 忙碌 / 请勿打扰: a dropdown under the chip, public/dropdown.js), and the mic: which one, a live level meter, 降噪 and
// 开放麦克风 / 按住说话 (public/voice-settings.js; the choices persist as in 设置 → 语音). The mic is only asked for when
// you press 「试一下麦克风」 (or already allowed earlier: then the meter just starts); no mic, or no permission, still
// boards (you hear nothing and say nothing until it's fixed, as before). It shows every time, and one click (or Enter)
// goes: 上船 stays the big button. A look saves before boarding; status changes are queued until welcome (flush()).
// Built to be shared: it renders into any element, knows nothing of app.js, and a guest page can leave parts out.
//   const pre = createPrejoin({ root, name, info: fetch('/prejoin/main')..., otterImgs: promise, openDress, dressOpen });
//   pre.standIn()   the stand-in "me" for the dress-up panel before boarding ({id, seed, look})
//   pre.queue(msg)  a message for the office before boarding ({t:'look'} / {t:'status'}); pre.flush(send) on welcome
//   pre.setLook(l)  the look was saved (the dress-up panel's saved hook)
//   pre.stop()      boarding: the mic preview stops (voice takes the mic itself)
import { buildSheet, lookFor, frames16, otterArtReady } from '/looks.js';
import { icon, label } from '/icons.js';
import { statusOf, statusLine, STATES } from '/status.js';
import { dropdown } from '/dropdown.js';
import { t, tParts, tr, onLang } from '/i18n.js';
import { voiceSection, micPicker } from '/voice-settings.js';
import { micConstraints, micDevice, denoiseWanted, agoraDenoiseLevel, keepRunning } from '/denoise.js';
import { micClip } from '/mic-clip.js';

const CSS = `
#join{z-index:15} .overlay{z-index:19} /* over the chat log and the bar; an error card (offline, the map failed) over it and over the duo's bubble, picker and card (duo.js, 16-18); the dress-up panel (20) and the loading screen (70) above */
#join .card.pj{width:min(680px,calc(100vw - 24px));max-height:calc(100vh - 24px - var(--st,0px) - var(--sb,0px));max-height:calc(100dvh - 24px - var(--st,0px) - var(--sb,0px));overflow:auto;box-sizing:border-box;padding:18px 22px 16px;box-shadow:0 6px 0 #1b1410}
.pj-head{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin:0 0 12px}
.pj-head .pj-title{font-size:20px;font-weight:700}
.pj-head .pj-hi{flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
#join .pj button{width:auto;font:inherit;padding:4px 12px;border:2px solid #1b1410;border-radius:999px;background:#fff;cursor:pointer;color:#1b1410}
#join .pj button:disabled{opacity:.45;cursor:default}
#join .pj input[type=checkbox],#join .pj input[type=radio]{width:auto;margin:0;padding:0}
#join .pj .pj-chip{display:inline-flex;align-items:center;gap:6px;font-size:13px;max-width:100%}
#join .pj .pj-chip[aria-expanded=true]{background:#FDD03B}
.pj-main{display:grid;grid-template-columns:200px 1fr;gap:16px;align-items:start}
.pj-stage{background:#e9d8b4;border:2px solid #1b1410;border-radius:14px;padding:10px;display:flex;flex-direction:column;align-items:center;gap:8px;position:relative}
.pj-stage canvas{width:160px;height:160px;image-rendering:pixelated;display:block;cursor:pointer}
#join .pj .pj-dress{display:inline-flex;align-items:center;gap:6px;background:#FDD03B}
#join .pj .pj-dress.nudge{animation:pjnudge 1.2s ease-in-out infinite}
@keyframes pjnudge{50%{box-shadow:0 0 0 4px rgba(253,208,59,.6)}}
.pj-bubble{position:relative;background:#fff;border:2px solid #1b1410;border-radius:12px;padding:6px 10px;font-size:12px;line-height:1.4;text-align:center}
.pj-bubble::after{content:"";position:absolute;left:50%;bottom:-8px;margin-left:-6px;border:6px solid transparent;border-top-color:#1b1410;border-bottom:0}
.pj-sec h4{margin:0 0 6px;font-size:14px;display:flex;align-items:center;gap:6px}
.pj-sec{min-width:0}.pj-mic{min-width:0;background:#fff;border:2px solid #1b1410;border-radius:14px;padding:10px 12px}
.pj-meter{display:flex;gap:3px;height:16px;margin:6px 0 4px}
.pj-meter i{flex:1;background:#e9d8b4;border:1px solid #1b1410;border-radius:2px}
.pj-meter i.lit{background:#22c55e}.pj-meter i.lit.y{background:#FDD03B}.pj-meter i.lit.r{background:#ef4444}
.pj-micstate{font-size:13px;display:flex;gap:6px;align-items:flex-start;flex-wrap:wrap}
.pj-micstate .t{flex:1;min-width:160px}
.pj-micnote{font-size:12px;color:#6b5a44;min-height:18px;display:flex;align-items:center;gap:5px}
.pj-meter-label{font-size:12px;color:#6b5a44;margin-top:6px}
.pj-clip{min-width:0;font-size:12px;color:#6b5a44;margin:6px 0}.pj-clip button{margin:4px 0}.pj-clip audio{display:block;width:100%;min-width:0;max-width:300px;margin-top:4px}.pj-clip audio[hidden]{display:none}
.pj-mic .voice-set{margin-top:6px;border-top:1px dashed #1b1410;padding-top:6px}
.pj-mic .voice-set .hint{display:none}
.pj-mic .voice-set label{margin:2px 0}
.pj-mic .voice-set hr{display:none}
.pj-mic .pj-modes{display:flex;gap:4px 14px;flex-wrap:wrap}
.pj-mic .dev{margin:6px 0 2px;display:flex}
#join .pj .pj-mic .dev .dd-field{font-size:13px;flex:1;min-width:0;padding:4px 8px 4px 10px;border-radius:8px}
#join .pj .pj-go{display:block;width:100%;margin:14px 0 0;padding:12px;font-size:18px;font-weight:700;border:3px solid #1b1410;border-radius:999px;background:#FDD03B;box-shadow:0 4px 0 #1b1410}
#join .pj .pj-go:active{transform:translateY(2px);box-shadow:0 2px 0 #1b1410}
.pj-go kbd{font:600 11px/1 ui-monospace,Menlo,monospace;border:1px solid #1b1410;border-radius:4px;padding:2px 4px;margin-left:8px;background:#fff8e7;vertical-align:2px}
.pj-help{display:flex;flex-wrap:wrap;gap:4px 14px;justify-content:center;margin:10px 0 0;font-size:12px;color:#6b5a44}
.pj-help kbd{font:600 11px/1 ui-monospace,Menlo,monospace;border:1px solid #9a8a74;border-radius:4px;padding:1px 4px;background:#fff}
@media (max-width:640px){
 #join .card.pj{padding:14px 14px 12px;width:calc(100vw - 16px);max-height:calc(100vh - 16px - var(--st,0px) - var(--sb,0px));max-height:calc(100dvh - 16px - var(--st,0px) - var(--sb,0px))}
 .pj-main{grid-template-columns:1fr;gap:10px}
 .pj-stage{flex-direction:row;justify-content:center;padding:8px;gap:10px}
 .pj-stage canvas{width:104px;height:104px}
 .pj-stage .pj-side{display:flex;flex-direction:column;align-items:center;gap:6px;max-width:170px}
 .pj-go kbd,.pj-help .k{display:none}
}
@media (prefers-reduced-motion:reduce){#join .pj .pj-dress.nudge{animation:none;box-shadow:0 0 0 3px rgba(253,208,59,.8)}}`;

const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };
const SEGS = 16;

/**
 * @param o.root where it goes (app.js: the join card's #signedIn; the card gets the class "pj")
 * @param o.name the name shown (你好，…)
 * @param o.info Promise of {seed, look, status} (GET /prejoin/<room>, src/routes/prejoin.ts) or null (no preview, no nudge)
 * @param o.otterImgs Promise of the body sheets (looks.js BODIES order)
 * @param o.onLook (look, images) => void: the current preview or saved look, without waiting to board
 * @param o.openDress () => void: opens the dress-up panel (with standIn() as "me"); none: no 装扮 button
 * @param o.dressOpen () => boolean: the panel is up (Enter belongs to it then)
 * @param o.status false: leave out the status (a guest has none)
 */
export function createPrejoin(o) {
  if (!document.getElementById('pj-css')) { const s = el('style'); s.id = 'pj-css'; s.textContent = CSS; document.head.append(s); }
  const card = o.root.closest('.card'); card?.classList.add('pj');
  const oldTitle = card?.querySelector(':scope > b'); if (oldTitle) oldTitle.style.display = 'none'; // the head below has it
  const pending = { look: null, status: null };
  let info = null, look = null, seed = '', imgs = null, sheet = null, sheetKey = '';

  // ---- the head: title, hello, my status
  const head = el('div', 'pj-head');
  const hi = el('div', 'pj-hi'), nm = el('b', null, o.name || ''); nm.id = 'hello';
  const drawHi = () => hi.replaceChildren(...tParts('join.hello', { name: nm })); drawHi();
  head.append(tr(el('span', 'pj-title'), 'join.title'), hi);
  // my status: the UI's dropdown (public/dropdown.js) under the chip, over the card; a pick holds at once (queued for
  // the office, flush()). A note or an expiry is set on board (the chip in the bar, 设置 → 我的状态).
  let myStatus = null, pickedAt = 0, office; // what holds (from the office, or picked here), when picked; the office's own (undefined until /prejoin answers)
  const HINTS = { away: 'join.status.hint.away', dnd: 'join.status.hint.dnd' };
  const chipOf = (s) => (s ? s.s : 'available');
  const options = () => Object.entries(STATES).map(([id, d]) => ({ value: id, text: d.name, icon: d.icon, hint: HINTS[id] && t(HINTS[id]) }));
  // label: a getter, read by the dropdown on every draw (its aria-label), so it follows a language change
  const status = dropdown({ className: 'pj-chip', get label() { return t('status.title'); }, title: t('join.status.tip'), always: true,
    options: options(),
    value: 'available',
    render: (_, b) => { const s = statusOf({ status: myStatus }), d = STATES[s?.s ?? 'available'], t = el('span', 'dd-text', statusLine(s)), c = icon('caret_down'); c.classList.add('dd-caret'); b.replaceChildren(icon(d.icon), t, c); },
    onChange: (id) => {
      const cur = statusOf({ status: myStatus });
      if (id === chipOf(cur) && !cur?.auto) return; // the same state again: keep its note and expiry
      const held = statusOf({ status: office });
      if (office !== undefined && id === chipOf(held) && !held?.auto) { pending.status = null; myStatus = office; drawChip(); return; } // back to what the office holds (its note and expiry too)
      const m = id === 'available' ? { t: 'status', s: 'available' } : { t: 'status', s: id };
      pending.status = m; pickedAt = Date.now();
      myStatus = id === 'available' ? null : { s: id };
      drawChip();
    } });
  const chip = status.el;
  const drawChip = () => status.set(chipOf(statusOf({ status: myStatus })));
  onLang(() => { drawHi(); status.el.title = t('join.status.tip'); status.setOptions(options()); });
  if (o.status !== false) { head.append(chip); drawChip(); }

  // ---- my otter: turning on the spot (idle), 装扮 under it
  const stage = el('div', 'pj-stage'), cv = el('canvas'); cv.width = cv.height = 32; tr(cv, null, null, { title: 'join.dress' });
  const side = el('div', 'pj-side'), bubble = tr(el('div', 'pj-bubble'), 'join.dressNudge'); bubble.style.display = 'none';
  const dressBtn = el('button', 'pj-dress'); dressBtn.type = 'button';
  const drawDress = () => label(dressBtn, 'shirt', t('join.dress')); drawDress(); onLang(drawDress);
  if (o.openDress) { dressBtn.onclick = () => o.openDress(); cv.onclick = () => o.openDress(); side.append(bubble, dressBtn); } else cv.style.cursor = 'default';
  stage.append(cv, side);
  const t0 = performance.now();
  let raf = 0;
  function tick(t) {
    raf = requestAnimationFrame(tick);
    if (!imgs || !look) return;
    const key = JSON.stringify(look) + imgs.map((i) => (i ? 1 : 0)).join(''); // a sheet that came in on the retry (app.js): build again
    if (key !== sheetKey) { sheetKey = key; sheet = buildSheet(imgs, look); }
    // a slow turn, a step every 0.7 s, breathing (the 3D idle) all the while; front-facing first
    const el_ = t - t0, d = Math.floor(el_ / 700) % 16, fr = frames16('idle', d), f = fr[Math.floor(el_ / 160) % fr.length];
    const g = cv.getContext('2d'); g.imageSmoothingEnabled = false; g.clearRect(0, 0, 32, 32);
    g.drawImage(sheet, (f % 12) * 32, Math.floor(f / 12) * 32, 32, 32, 0, 0, 32, 32);
  }
  raf = requestAnimationFrame(tick);
  Promise.all([o.otterImgs, otterArtReady]).then(([i]) => { imgs = i; sheetKey = ''; if (look) o.onLook?.(look, imgs); });
  Promise.resolve(o.info).then((r) => {
    setPreviewProvider(r?.flags);
    info = r;
    // /prejoin didn't answer: an otter from this name (not the saved one, so no 装扮 nudge), and 装扮 still works (what's
    // saved there is sent on boarding, as always)
    if (!r) { seed = o.name || 'me'; look = lookFor(null, seed); o.onLook?.(look, imgs); return; }
    seed = r.seed || ''; look = lookFor(r.look, seed);
    o.onLook?.(look, imgs);
    if (o.status !== false) { office = r.status ?? null; myStatus = pending.status ? myStatus : office; drawChip(); }
    if (r.profileUnavailable) { tr(bubble, 'join.profileUnavailable'); bubble.style.display = ''; }
    else if ((!r.look || r.dressed === false) && !pending.look) { bubble.style.display = ''; dressBtn.classList.add('nudge'); }
  }, () => setPreviewProvider(null));

  // ---- the mic
  const mic = el('div', 'pj-mic');
  const micHead = el('h4'); micHead.append(icon('mic'), tr(el('span'), 'join.mic.title'));
  const state = el('div', 'pj-micstate'), stText = el('span', 't'), tryBtn = el('button'); tryBtn.type = 'button';
  state.append(stText, tryBtn);
  const meter = el('div', 'pj-meter'); meter.setAttribute('role', 'meter'); tr(meter, null, null, { 'aria-label': 'join.mic.meter' }); meter.setAttribute('aria-valuemin', '0'); meter.setAttribute('aria-valuemax', String(SEGS));
  const segs = Array.from({ length: SEGS }, (_, i) => { const s = el('i'); if (i >= SEGS * 0.85) s.classList.add('r'); else if (i >= SEGS * 0.6) s.classList.add('y'); return s; });
  meter.append(...segs); meter.style.display = 'none';
  const micNote = el('div', 'pj-micnote');
  const clipBox = el('div', 'pj-clip'), clipInfo = tr(el('div'), 'join.mic.clipInfo');
  const clipBtn = el('button', 'pj-clip-button'); clipBtn.type = 'button';
  const clipStatus = el('div', 'pj-clip-status'); clipStatus.setAttribute('role', 'status');
  const clipAudio = el('audio', 'pj-clip-audio'); clipAudio.controls = true; clipAudio.setAttribute('controlsList', 'nodownload'); clipAudio.preload = 'metadata'; clipAudio.hidden = true;
  tr(clipAudio, null, null, { 'aria-label': 'join.mic.clipPlayback' });
  clipBox.append(clipInfo, clipBtn, clipStatus, clipAudio);
  let previewProvider = 'unknown'; // only the server's prejoin flags select a provider
  let stopped = false, wanted = false, autoStart = true, current = null, micPending = Promise.resolve(), waiting = 0;
  const picker = micPicker({ onPick: () => { if (wanted) startMic(); } });
  const vs = voiceSection({ picker: false, nativePreview: () => previewProvider === 'agora',
    nativePreviewLevel: () => !wanted ? null : current?.kind === 'agora' ? current.mic.denoiserLevel() : { status: 'applying', level: null } });
  const meterLabel = el('div', 'pj-meter-label'); meterLabel.style.display = 'none';
  // the two voice modes side by side (they're the same radios as 设置 → 语音)
  const modes = el('div', 'pj-modes'); for (const r of vs.querySelectorAll('[data-group=voiceMode]')) modes.append(r.closest('label')); vs.append(modes);
  mic.append(micHead, state, picker, meterLabel, meter, micNote, clipBox, vs);

  let meterRaf = 0, heardAt = 0, gen = 0, noteMode = '';
  let capture = null, clipUrl = null, clipState = '', clipMode = '', renderedClipState = null, renderedClipMode = '';
  const CLIP_TEXT = { Recording: 'join.mic.clipRecording', Cancelled: 'join.mic.clipCancelled', Failed: 'join.mic.clipFailed' };
  const CLIP_MODE = { 'join.mic.inputMeter': 'join.mic.clipInput', 'join.mic.denoiseOffMeter': 'join.mic.clipOff',
    'join.mic.processedMeter': 'join.mic.clipProcessed', 'join.mic.fallbackMeter': 'join.mic.clipFallback' };
  function drawClip() {
    const buttonKey = capture ? 'join.mic.clipCancel' : 'join.mic.clipRecord';
    if (clipBtn.dataset.i18n !== buttonKey) tr(clipBtn, buttonKey);
    let ready = false;
    try { ready = !!current?.startClip && live(gen, current) && mic.dataset.state === 'live' && !current.suspended?.(); } catch {}
    const disabled = !capture && !ready;
    if (clipBtn.disabled !== disabled) clipBtn.disabled = disabled;
    if (renderedClipState !== clipState || renderedClipMode !== clipMode) {
      clipStatus.textContent = clipState === 'ready' ? t('join.mic.clipReady', { mode: t(CLIP_MODE[clipMode]) }) : clipState ? t(CLIP_TEXT[clipState]) : '';
      renderedClipState = clipState; renderedClipMode = clipMode;
    }
    if (clipAudio.hidden !== !clipUrl) clipAudio.hidden = !clipUrl;
  }
  function clearClip(status = '') {
    const old = capture; capture = null;
    if (old) { old.clip.cancel(); try { old.session.stopClip(); } catch {} }
    clipAudio.pause(); clipAudio.removeAttribute('src'); clipAudio.load();
    if (clipUrl) URL.revokeObjectURL(clipUrl);
    clipUrl = null; clipMode = ''; clipState = status; drawClip();
  }
  function startClip() {
    const session = current, my = gen;
    if (!session?.startClip || !live(my, session) || mic.dataset.state !== 'live' || session.suspended?.()) return;
    clearClip();
    const mode = clipSource(session);
    const clip = micClip({ seconds: 4, timeoutMs: 8000,
      onReady: (blob, token) => {
        if (!capture || capture.clip !== clip || capture.session !== session || !clip.isCurrent(token) || !live(my, session)) return;
        if (clipSource(session) !== mode || session.suspended?.()) { clearClip('Failed'); return; }
        capture = null; clip.cancel(); try { session.stopClip(); } catch {}
        try { clipUrl = URL.createObjectURL(blob); clipAudio.src = clipUrl; clipMode = mode; clipState = 'ready'; }
        catch { clearClip('Failed'); return; }
        drawClip();
      },
      onError: (token) => { if (capture?.clip === clip && clip.isCurrent(token)) clearClip('Failed'); },
    });
    const token = clip.start(); capture = { clip, token, session, mode }; clipState = 'Recording'; drawClip();
    try { session.startClip(clip, token); } catch { clearClip('Failed'); }
  }
  clipBtn.onclick = (event) => { if (!event.isTrusted) return; if (capture) clearClip('Cancelled'); else startClip(); };
  const resumeBtn = tr(el('button', 'pj-resume'), 'join.mic.resumeTest'); resumeBtn.type = 'button';
  resumeBtn.onclick = () => { try { current?.resume?.(); } catch {} };
  const heardLabel = tr(el('span'), 'join.mic.heard');
  function showNote(mode) {
    if (noteMode === mode) return;
    noteMode = mode;
    micNote.replaceChildren(...(mode === 'resume' ? [resumeBtn] : mode === 'heard' ? [icon('check'), heardLabel] : []));
  }
  // the mic's states: the line's text and the button's (none: no button)
  const MIC = {
    idle: { text: 'join.mic.idle', btn: 'join.mic.try' },
    asking: { text: 'join.mic.asking', btn: 'join.mic.cancel' },
    live: { text: 'join.mic.live', btn: 'join.mic.stopTest' },
    denied: { text: 'join.mic.denied', btn: 'join.mic.retry' },
    none: { text: 'join.mic.none', btn: 'join.mic.retry' },
    busy: { text: 'join.mic.busy', btn: 'join.mic.retry' },
    previewUnavailable: { text: 'join.mic.previewUnavailable', btn: 'join.mic.retry' },
    unsupported: { text: 'join.mic.unsupported' },
  };
  function setState(kind) {
    const S = MIC[kind];
    mic.dataset.state = kind;
    tr(stText, S.text);
    tryBtn.style.display = S.btn ? '' : 'none'; if (S.btn) label(tryBtn, kind === 'live' ? 'mic_off' : 'mic', t(S.btn));
    meter.style.display = kind === 'live' ? '' : 'none';
    meterLabel.style.display = kind === 'live' ? '' : 'none';
    if (kind !== 'live') showNote('');
    drawClip();
  }
  const live = (my, session) => !stopped && wanted && gen === my && (!session || current === session);
  function closeCurrent() {
    cancelAnimationFrame(meterRaf); meterRaf = 0; heardAt = 0;
    clearClip();
    const old = current; current = null;
    try { old?.close(); } catch {}
    if (old?.released) {
      waiting++;
      const drain = Promise.resolve().then(() => old.released());
      micPending = Promise.all([micPending, drain]).then(() => {}).catch(() => {}).finally(() => { waiting--; });
    }
  }
  function stopMic(show = true) {
    gen++; wanted = false; closeCurrent();
    vs.sync();
    if (show) setState('idle');
  }
  function failMic(my, kind) {
    if (!live(my)) return;
    stopMic(false); autoStart = false; setState(kind);
  }
  function setPreviewProvider(flags) {
    const next = Array.isArray(flags) ? (flags.includes('voice-agora') ? 'agora' : 'sfu') : 'unknown';
    if (previewProvider === next) return;
    previewProvider = next; vs.sync();
    if (wanted && !stopped) startMic();
  }
  function meterMode(session) {
    if (session.kind !== 'agora') return 'join.mic.inputMeter';
    if (!denoiseWanted()) return 'join.mic.denoiseOffMeter';
    return session.mic.denoiser() === 'on' ? 'join.mic.processedMeter' : 'join.mic.fallbackMeter';
  }
  function clipSource(session) {
    if (session.kind !== 'agora') return 'join.mic.inputMeter';
    const state = session.mic.denoiser();
    return state === 'on' ? 'join.mic.processedMeter' : state === 'off' ? 'join.mic.denoiseOffMeter' : 'join.mic.fallbackMeter';
  }
  function showMeter(my, session) {
    if (!live(my, session)) return;
    setState('live'); picker.refresh();
    const loop = () => {
      if (!live(my, session)) return;
      const waitingForAudio = !!session.suspended?.();
      if (waitingForAudio) {
        if (capture?.session === session || clipUrl) clearClip('Failed');
        drawClip();
        heardAt = 0; meter.style.display = 'none'; meterLabel.style.display = 'none';
        if (stText.dataset.i18n !== 'join.mic.waiting') tr(stText, 'join.mic.waiting');
        showNote('resume'); meterRaf = requestAnimationFrame(loop); return;
      }
      meter.style.display = ''; meterLabel.style.display = '';
      if (stText.dataset.i18n !== 'join.mic.live') tr(stText, 'join.mic.live');
      let db;
      try { db = session.db(); } catch { failMic(my, 'previewUnavailable'); return; }
      if (!Number.isFinite(db)) { failMic(my, 'previewUnavailable'); return; }
      const key = meterMode(session);
      drawClip();
      if (meterLabel.dataset.i18n !== key) { tr(meterLabel, key); tr(meter, null, null, { 'aria-label': key }); }
      const lvl = Math.max(0, Math.min(1, (db + 60) / 50)); // -60 dB .. -10 dB
      const n = Math.round(lvl * SEGS); segs.forEach((g, i) => g.classList.toggle('lit', i < n)); meter.setAttribute('aria-valuenow', String(n));
      const now = performance.now(); if (n >= 5) heardAt = now;
      showNote(heardAt && now - heardAt < 2500 ? 'heard' : '');
      meterRaf = requestAnimationFrame(loop);
    };
    loop();
  }
  async function openInput(my) {
    const session = { kind: 'input', stream: null, ctx: null,
      close() { for (const t of this.stream?.getTracks() || []) t.stop(); if (this.ctx && this.ctx.state !== 'closed') void this.ctx.close().catch(() => {}); } };
    current = session;
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia(micConstraints()); }
    catch (e) {
      const n = e?.name;
      failMic(my, n === 'NotAllowedError' || n === 'SecurityError' ? 'denied' : n === 'NotFoundError' || n === 'OverconstrainedError' ? 'none' : 'busy'); return;
    }
    if (!live(my, session)) { for (const t of stream.getTracks()) t.stop(); return; }
    session.stream = stream;
    stream.getAudioTracks()[0]?.addEventListener('ended', () => { if (live(my, session)) failMic(my, 'none'); });
    try {
      session.ctx = new AudioContext(); keepRunning(session.ctx);
      const an = session.ctx.createAnalyser(); an.fftSize = 1024; const buf = new Float32Array(an.fftSize);
      const source = session.ctx.createMediaStreamSource(stream); source.connect(an);
      let clipNode = null, clipGain = null;
      session.stopClip = () => {
        if (clipNode) { clipNode.onaudioprocess = null; try { source.disconnect(clipNode); } catch {} try { clipNode.disconnect(); } catch {} }
        try { clipGain?.disconnect(); } catch {}
        clipNode = null; clipGain = null;
      };
      session.startClip = (clip, token) => {
        session.stopClip();
        try {
          clipNode = session.ctx.createScriptProcessor(2048, 1, 1); clipGain = session.ctx.createGain(); clipGain.gain.value = 0;
          clipNode.onaudioprocess = (event) => { if (live(my, session) && session.ctx?.state === 'running') clip.push(event.inputBuffer, token); };
          source.connect(clipNode); clipNode.connect(clipGain); clipGain.connect(session.ctx.destination);
        } catch (e) { session.stopClip(); throw e; }
      };
      session.db = () => { an.getFloatTimeDomainData(buf); let sum = 0; for (const v of buf) sum += v * v; return 20 * Math.log10(Math.sqrt(sum / buf.length) + 1e-9); };
      session.suspended = () => session.ctx?.state !== 'running';
      session.resume = () => { try { void session.ctx?.resume().catch(() => {}); } catch {} };
    } catch { failMic(my, 'previewUnavailable'); return; }
    showMeter(my, session);
  }
  async function openAgora(my) {
    let module;
    try { module = await import('/rtc/agora-mic.ts'); } catch { failMic(my, 'previewUnavailable'); return; }
    if (!live(my)) return;
    let sdk;
    try { sdk = await module.loadAgoraSdk(); } catch { failMic(my, 'previewUnavailable'); return; }
    if (!live(my)) return;
    let session, track = null, frameAt = -Infinity, frameDb = -100, contextBlocked = false;
    const onContext = (state) => { contextBlocked = state !== 'running'; if (contextBlocked) frameAt = -Infinity; };
    const mic = module.createAgoraMicrophone({ microphoneId: micDevice(), denoiser: denoiseWanted(), denoiserLevel: agoraDenoiseLevel(), waitForDenoiser: true, onEvent: (event) => {
      if (!live(my, session)) return;
      if (event.type === 'denoiser-level' || event.type === 'denoiser') vs.sync();
      if (event.type === 'denoiser' && (capture?.session === session || clipUrl) && (capture?.mode || clipMode) !== clipSource(session)) clearClip('Failed');
      if (event.type === 'device-ended') failMic(my, 'none');
      else if (event.type === 'error') failMic(my, event.error?.reason === 'mic-denied' ? 'denied' : event.error?.reason === 'mic-missing' ? 'none' : 'busy');
    } });
    session = { kind: 'agora', mic, released: () => mic.released(), close: () => {
      try { track?.setAudioFrameCallback(null); } catch {}
      try { sdk.off('audio-context-state-changed', onContext); } catch {}
      mic.close();
    } }; current = session;
    try {
      sdk.on('audio-context-state-changed', onContext);
      track = await mic.start(); if (!live(my, session)) { session.close(); return; }
      // Agora permits one callback per track. Each installed callback captures its own clip and token so late
      // frames from an older recording cannot enter a later one; the same callback also drives the meter.
      const onFrame = (clip, token) => (buffer) => {
        if (!live(my, session) || contextBlocked) return;
        try {
          const data = buffer.getChannelData(0); if (!data.length) return;
          let sum = 0; for (const value of data) sum += value * value;
          const db = 20 * Math.log10(Math.sqrt(sum / data.length) + 1e-9);
          if (Number.isFinite(db)) { frameDb = db; frameAt = performance.now(); }
          clip?.push(buffer, token);
        } catch {}
      };
      session.startClip = (clip, token) => track.setAudioFrameCallback(onFrame(clip, token), 2048);
      session.stopClip = () => { if (live(my, session)) track.setAudioFrameCallback(onFrame(null, null), 2048); };
      session.stopClip();
      await mic.setMuted(false); if (!live(my, session)) { session.close(); return; }
      frameAt = -Infinity; // wait for a frame after the local microphone gate actually opens
      session.db = () => frameDb;
      session.suspended = () => contextBlocked || performance.now() - frameAt > 1500;
      session.resume = () => { try { Promise.resolve(sdk.resumeAudioContext()).catch(() => {}); } catch {} };
      showMeter(my, session);
    } catch (e) {
      if (!live(my, session)) { session.close(); return; }
      failMic(my, e?.reason === 'mic-denied' ? 'denied' : e?.reason === 'mic-missing' ? 'none' : 'previewUnavailable');
    }
  }
  function startMic() {
    if (stopped) return;
    wanted = true; const my = ++gen; closeCurrent(); setState('asking');
    vs.sync();
    // A browser permission prompt cannot be cancelled. Serialize capture attempts so a late result is stopped
    // before a new device is opened, and expose the drain to the on-board provider without delaying boarding.
    waiting++;
    micPending = micPending.then(async () => { if (!live(my)) return; if (previewProvider === 'agora') await openAgora(my); else await openInput(my); })
      .catch(() => failMic(my, 'previewUnavailable')).finally(() => { waiting--; });
  }
  vs.addEventListener('change', (e) => {
    if (e.target?.name !== 'denoise' && e.target?.dataset?.group !== 'denoiseKind' && e.target?.dataset?.group !== 'agoraDenoiseLevel') return;
    if (wanted && previewProvider === 'agora') startMic(); else clearClip();
  });
  onLang(() => { const k = mic.dataset.state; if (!k || !MIC[k]) return; renderedClipState = null; const note = [...micNote.childNodes]; setState(k); if (k === 'live') micNote.replaceChildren(...note); }); // the meter loop redraws the note itself
  tryBtn.onclick = () => { autoStart = false; if (wanted) stopMic(); else startMic(); };
  drawClip();
  if (!navigator.mediaDevices?.getUserMedia) setState('unsupported');
  else {
    setState('idle');
    // Allowed before (this site, this browser): no question to ask, so the meter just starts. Refused before: say so.
    navigator.permissions?.query({ name: 'microphone' }).then((p) => {
      if (stopped || !autoStart || mic.dataset.state !== 'idle') return;
      if (p.state === 'granted') startMic(); else if (p.state === 'denied') setState('denied');
    }).catch(() => {});
  }
  const onPageHide = () => { autoStart = false; if (!stopped) stopMic(); };
  addEventListener('pagehide', onPageHide);

  // ---- 上船 and the short help
  const go = el('button', 'pj-go'); go.type = 'button'; go.id = 'go'; go.append(tr(el('span'), 'join.go')); const k = el('kbd', null, 'Enter'); go.append(k);
  const help = el('div', 'pj-help');
  const drawHelp = () => { help.innerHTML = t('join.help'); }; drawHelp(); onLang(drawHelp); // the catalog's own markup (kbd, span)
  const main = el('div', 'pj-main'), right = el('div', 'pj-sec');
  right.append(mic);
  main.append(stage, right);
  o.root.replaceChildren(head, main, go, help);
  // the old help paragraph (a cached index.html) goes: this one replaces it
  for (const p of card?.querySelectorAll(':scope > p') || []) if (!p.closest('#signedOut')) p.remove();

  // Enter boards (not while typing, in the dress-up panel, or on another button reached with the keyboard)
  const onKey = (e) => {
    if (e.key !== 'Enter' || e.isComposing || e.repeat || !o.root.isConnected || o.root.offsetParent === null) return;
    const join = o.root.closest('#join'); if (join && getComputedStyle(join).display === 'none') return;
    if (o.dressOpen?.()) return;
    const a = document.activeElement;
    if (a && (a.matches('input:not([type=checkbox]):not([type=radio]), textarea, select, audio[controls]') || a.isContentEditable)) return; // typing or using playback (a ticked box doesn't count)
    if (a && a !== go && a.matches('button, a[href]') && a.matches(':focus-visible')) return;
    e.preventDefault(); go.click();
  };
  addEventListener('keydown', onKey);
  go.addEventListener('click', () => stop());

  function stop() { if (stopped) return; stopped = true; stopMic(false); cancelAnimationFrame(raf); raf = 0; removeEventListener('keydown', onKey); removeEventListener('pagehide', onPageHide); }
  return {
    stop,
    released: () => micPending,
    releasing: () => waiting > 0,
    standIn: () => (look ? { id: 'me', seed, look } : null),
    queue(m) { if (m?.t === 'look') pending.look = m.look; else if (m?.t === 'status') { pending.status = m; pickedAt = Date.now(); } },
    setLook(l) { look = l; o.onLook?.(look, imgs); pending.look = null; bubble.style.display = 'none'; dressBtn.classList.remove('nudge'); },
    /** On board (the office's welcome): send what was changed here. Returns the look to show for me, if one was saved. */
    flush(send) {
      const l = pending.look;
      // a duration queued here (queue(): {for}) runs from when it was queued: send what's left of it
      let st = pending.status;
      if (st?.for) { const left = st.for - (Date.now() - pickedAt); st = left > 0 ? { ...st, for: Math.max(60_000, Math.round(left)) } : { t: 'status', s: 'available' }; }
      if (st) send(st);
      if (l) send({ t: 'look', look: l });
      pending.look = pending.status = null;
      return l;
    },
  };
}
